if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      connectedCallback() {
        // Move to a direct child of <body>. This theme's scroll-reveal animation
        // classes (and any other transformed/will-change ancestor) create a new
        // containing block, which would turn `position: fixed` into positioning
        // relative to that ancestor instead of the viewport. Moving out avoids that
        // regardless of where this snippet is rendered from. Re-parenting fires
        // connectedCallback again, so bail out once we're already there.
        if (this.parentElement !== document.body) {
          // A full product swap (e.g. a variant option linking to a different
          // product) replaces <product-info>'s contents with a fresh copy of this
          // element sharing the same id, while the previous instance is already
          // sitting in <body> from an earlier move. Clear it out first so the two
          // don't both end up stuck to the viewport.
          const stale = document.getElementById(this.id);
          if (stale && stale !== this) stale.remove();

          document.body.appendChild(this);
          return;
        }

        this.closeButton = this.querySelector('[data-sticky-atc-close]');
        this.dismissed = false;
        this.closeButton?.addEventListener('click', this.onCloseClick.bind(this));

        this.initLineItemSync();

        // Since this element lives in <body> rather than inside the section's own
        // wrapper, the theme editor's section re-render/removal has no way to find
        // and clean it up on its own (e.g. disabling the "show sticky ATC" setting,
        // or reordering/removing the section) — it would otherwise stay orphaned
        // until a full page reload. Remove it ourselves when Shopify tells us this
        // element's own section is going away.
        this.onSectionUnload = this.onSectionUnload.bind(this);
        document.addEventListener('shopify:section:unload', this.onSectionUnload);

        this.target = document.getElementById(this.dataset.observeTarget);
        if (!this.target) return;

        this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
          threshold: 0,
        });
        this.observer.observe(this.target);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      }

      onSectionUnload(event) {
        if (event.detail?.sectionId === this.dataset.sectionId) {
          this.remove();
        }
      }

      // The line item field mirror isn't a real form field (no name/form attribute):
      // it just keeps its value in sync both ways with the main required field, so
      // there's a single source of truth actually submitted with the form.
      initLineItemSync() {
        this.lineItemInput = this.querySelector('[data-sticky-line-item-mirror]');
        if (!this.lineItemInput) return;

        const realInput = document.getElementById(this.lineItemInput.dataset.stickyLineItemMirror);
        if (!realInput) return;

        this.lineItemInput.value = realInput.value;

        this.lineItemInput.addEventListener('input', () => {
          realInput.value = this.lineItemInput.value;
          realInput.dispatchEvent(new Event('input', { bubbles: true }));
        });

        realInput.addEventListener('input', () => {
          this.lineItemInput.value = realInput.value;
        });
      }

      onIntersect(entries) {
        const entry = entries[entries.length - 1];

        if (entry.isIntersecting) {
          // Main buy button is back in view: hide and re-arm the close dismissal.
          this.dismissed = false;
          this.setStuck(false);
          return;
        }

        // Only stick once the main buy button has scrolled above the viewport,
        // not before it has been reached on the way down.
        if (entry.boundingClientRect.top < 0) {
          this.setStuck(!this.dismissed);
        }
      }

      onCloseClick() {
        this.dismissed = true;
        this.setStuck(false);
      }

      setStuck(stuck) {
        this.classList.toggle('is-stuck', stuck);
        this.toggleAttribute('inert', !stuck);
      }
    }
  );
}
